import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ViewEncapsulation,
} from '@angular/core';
import { InAppBrowserUtils } from '../../utils/in-app-browser-utils';

interface ParsedElement {
  type:
    | 'header'
    | 'paragraph'
    | 'list'
    | 'bold'
    | 'italic'
    | 'underline'
    | 'strikethrough'
    | 'link';
  content: string;
  level?: number; // For headers (1-6)
  url?: string; // For links
  formattedContent?: ParsedElement[]; // For list items that contain formatting
}

@Component({
    selector: 'tidy-markdown',
    templateUrl: './tidy-markdown.component.html',
    styleUrls: ['./tidy-markdown.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TidyMarkdownComponent implements OnChanges {
  @Input() markdownContent: string = '';
  @Input() allBold = false;

  parsedElements: ParsedElement[] = [];

  constructor(private inAppBrowserUtils: InAppBrowserUtils) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.markdownContent) {
      this.parseMarkdown(changes.markdownContent.currentValue || '');
    }
  }

  private parseMarkdown(markdown: string): void {
    if (!markdown) {
      this.parsedElements = [];
      return;
    }

    const lines = markdown.trim().split('\n');
    const elements: ParsedElement[] = [];

    for (const line of lines) {
      const trimmedLine = line.trim();

      if (!trimmedLine) continue;

      // Headers (#, ##, ###, etc.)
      const headerMatch = trimmedLine.match(/^(#+)\s+(.*)/);
      if (headerMatch) {
        const level = headerMatch[1].length;
        elements.push({
          type: 'header',
          content: headerMatch[2].trim(),
          level,
        });
        continue;
      }

      // Basic Unordered Lists (*, -)
      const listMatch = trimmedLine.match(/^[-*]\s+(.*)/);
      if (listMatch) {
        const listContent = listMatch[1].trim();
        const formattedContent = this.processFormattedContent(listContent);
        elements.push({
          type: 'list',
          content: listContent,
          formattedContent:
            formattedContent.length > 0 ? formattedContent : undefined,
        });
        continue;
      }

      // Process any line that might contain mixed formatting
      const formattedContent = this.processFormattedContent(trimmedLine);
      if (formattedContent.length > 0) {
        elements.push({
          type: 'paragraph',
          content: trimmedLine,
          formattedContent,
        });
      } else {
        // If no formatting found, add as simple paragraph
        elements.push({ type: 'paragraph', content: trimmedLine });
      }
    }

    this.parsedElements = elements;
  }

  private processFormattedContent(content: string): ParsedElement[] {
    const formattedContent: ParsedElement[] = [];
    let remainingContent = content;

    while (remainingContent) {
      // Bold text
      const boldMatch = remainingContent.match(/\*(.*?)\*/);
      if (boldMatch) {
        const beforeBold = remainingContent.slice(
          0,
          remainingContent.indexOf(boldMatch[0])
        );
        if (beforeBold) {
          formattedContent.push({ type: 'paragraph', content: beforeBold });
        }
        formattedContent.push({ type: 'bold', content: boldMatch[1] });
        remainingContent = remainingContent.slice(
          remainingContent.indexOf(boldMatch[0]) + boldMatch[0].length
        );
        continue;
      }

      // Italic text
      const italicMatch = remainingContent.match(/_(.*?)_/);
      if (italicMatch) {
        const beforeItalic = remainingContent.slice(
          0,
          remainingContent.indexOf(italicMatch[0])
        );
        if (beforeItalic) {
          formattedContent.push({ type: 'paragraph', content: beforeItalic });
        }
        formattedContent.push({ type: 'italic', content: italicMatch[1] });
        remainingContent = remainingContent.slice(
          remainingContent.indexOf(italicMatch[0]) + italicMatch[0].length
        );
        continue;
      }

      // Underline text
      const underlineMatch = remainingContent.match(/__(.*?)__/);
      if (underlineMatch) {
        const beforeUnderline = remainingContent.slice(
          0,
          remainingContent.indexOf(underlineMatch[0])
        );
        if (beforeUnderline) {
          formattedContent.push({
            type: 'paragraph',
            content: beforeUnderline,
          });
        }
        formattedContent.push({
          type: 'underline',
          content: underlineMatch[1],
        });
        remainingContent = remainingContent.slice(
          remainingContent.indexOf(underlineMatch[0]) + underlineMatch[0].length
        );
        continue;
      }

      // Strikethrough text
      const strikethroughMatch = remainingContent.match(/~~(.*?)~~/);
      if (strikethroughMatch) {
        const beforeStrikethrough = remainingContent.slice(
          0,
          remainingContent.indexOf(strikethroughMatch[0])
        );
        if (beforeStrikethrough) {
          formattedContent.push({
            type: 'paragraph',
            content: beforeStrikethrough,
          });
        }
        formattedContent.push({
          type: 'strikethrough',
          content: strikethroughMatch[1],
        });
        remainingContent = remainingContent.slice(
          remainingContent.indexOf(strikethroughMatch[0]) +
            strikethroughMatch[0].length
        );
        continue;
      }

      // Links
      const linkMatch = remainingContent.match(/\[(.*?)\]\((.*?)\)/);
      if (linkMatch) {
        const beforeLink = remainingContent.slice(
          0,
          remainingContent.indexOf(linkMatch[0])
        );
        if (beforeLink) {
          formattedContent.push({ type: 'paragraph', content: beforeLink });
        }
        formattedContent.push({
          type: 'link',
          content: linkMatch[1],
          url: linkMatch[2],
        });
        remainingContent = remainingContent.slice(
          remainingContent.indexOf(linkMatch[0]) + linkMatch[0].length
        );
        continue;
      }

      // Auto-detect plain URLs (e.g., google.com, https://google.com)
      const urlRegex =
        /(https?:\/\/[\w.-]+(?:\/[\w\-.~:/?#[\]@!$&'()*+,;=%]*)?|www\.[\w.-]+(?:\/[\w\-.~:/?#[\]@!$&'()*+,;=%]*)?|[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/[\w\-.~:/?#[\]@!$&'()*+,;=%]*)?)/;
      const urlMatch = remainingContent.match(urlRegex);
      if (urlMatch) {
        const beforeUrl = remainingContent.slice(0, urlMatch.index);
        if (beforeUrl) {
          formattedContent.push({ type: 'paragraph', content: beforeUrl });
        }
        let url = urlMatch[0];
        let display = url;

        // Add https:// if no protocol is specified
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'https://' + url;
        }

        formattedContent.push({
          type: 'link',
          content: display,
          url: url,
        });
        remainingContent = remainingContent.slice(
          urlMatch.index + urlMatch[0].length
        );
        continue;
      }

      // If no more formatting found, add remaining content as paragraph
      if (remainingContent) {
        formattedContent.push({ type: 'paragraph', content: remainingContent });
        break;
      }
    }
    return formattedContent;
  }

  openLink(url: string): void {
    this.inAppBrowserUtils.openUrl(url);
  }
}
